"use client";

import { useMemo, useCallback } from "react";
import { Armchair, CalendarClock, ChevronRight } from "lucide-react";
import { IconTextRow } from "~/components/ui/icon-text-row";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import { useSession } from "next-auth/react";
import Link from "next/link";
import { Button, buttonVariants } from "~/components/ui/button";
import { api } from "~/trpc/react";
import { Skeleton } from "~/components/ui/skeleton";
import { type VariantProps } from "class-variance-authority";
import { parseAsStringEnum, useQueryState } from "nuqs";
import { cn } from "~/lib/utils";
import { format, isToday, isTomorrow } from "date-fns";
import { srLatn } from "date-fns/locale";
import { useSearchParams } from "next/navigation";
import { EUR } from "~/lib/currency";
import { TherapyRelationshipType } from "~/prisma/browser";
import { useDiscount } from "~/hooks/use-discount";
import { useAvailabilityFromBatch } from "./availability-batch-context";
import { capturePostHogEvent } from "~/lib/posthog/client";
import { resolveTherapyTypeQuery } from "~/lib/therapy-type-query";

const formatDateSerbian = (date: Date) => {
  const time = format(date, "HH:mm");

  if (isToday(date)) {
    return `već danas u ${time}`;
  }

  if (isTomorrow(date)) {
    return `već sutra u ${time}`;
  }

  return `${format(date, "do LLLL", { locale: srLatn })} u ${time}`;
};

interface TherapistTeaserProps {
  therapistSlug: string;
  withScarcityMessage?: boolean;
  variant?: "default" | "sidebar";
  availabilitySummary?: {
    firstAvailableDay: Date | null;
    spotsAvailableThisWeek?: number;
  } | null;
  isLoading?: boolean;
}
export const TherapistTeaser = ({
  withScarcityMessage = true,
  variant = "default",
  therapistSlug,
  availabilitySummary,
  isLoading,
}: TherapistTeaserProps) => {
  const batch = useAvailabilityFromBatch(therapistSlug);
  // Use the batch when a provider is mounted AND it has data for this slug.
  // If the batch has finished without an entry (chunk error, slug mismatch,
  // or full batch failure), fall back to the per-therapist query so the card
  // never silently shows "no slots" purely because of a batch failure.
  const batchUsable =
    availabilitySummary === undefined &&
    batch.hasProvider &&
    batch.summary !== undefined;
  const waitingForBatch =
    availabilitySummary === undefined &&
    batch.hasProvider &&
    batch.isLoading &&
    !batch.hasFailed;

  const singleQueryEnabled =
    availabilitySummary === undefined &&
    !isLoading &&
    !batchUsable &&
    !waitingForBatch;

  const availability = api.calendar.getAvailabilitySummary.useQuery(
    {
      therapistSlug,
      includeWeeklySpots: withScarcityMessage,
    },
    {
      enabled: singleQueryEnabled,
      staleTime: 1000 * 60 * 60 * 1,
    },
  );

  const batchSummary = batchUsable ? (batch.summary ?? null) : undefined;
  const summary =
    availabilitySummary ?? batchSummary ?? availability.data ?? null;

  const showSkeleton =
    isLoading ||
    waitingForBatch ||
    (singleQueryEnabled && availability.isPending);

  if (showSkeleton) {
    return (
      <Skeleton
        className={cn(
          "w-full rounded-md",
          variant === "sidebar" ? "h-11" : "h-[20px]",
        )}
      />
    );
  }

  if (!summary) {
    if (variant === "sidebar") {
      return (
        <IconTextRow icon={CalendarClock}>
          <p className="text-muted-foreground text-xs">Dostupnost</p>
          <p className="text-foreground text-sm font-medium">
            Trenutno nema slobodnih termina
          </p>
        </IconTextRow>
      );
    }
    return (
      <div className="flex flex-col items-center space-x-1 space-y-1 text-muted-foreground md:flex-row md:space-y-0">
        <div className="flex items-center text-xs">
          <CalendarClock className="mr-2 h-4 w-4" />
          <span>Trenutno nema slobodnih termina</span>
        </div>
      </div>
    );
  }

  const bookingMessage = summary.firstAvailableDay
    ? `Prvi slobodan termin je ${formatDateSerbian(summary.firstAvailableDay)}`
    : "Trenutno nema slobodnih termina";

  if (variant === "sidebar") {
    return (
      <IconTextRow icon={CalendarClock}>
        <p className="text-foreground text-sm">
          {summary.firstAvailableDay ? (
            <>
              <span className="text-muted-foreground">
                Prvi slobodan termin:{" "}
              </span>
              <span className="font-medium">
                {formatDateSerbian(summary.firstAvailableDay)}
              </span>
            </>
          ) : (
            "Trenutno nema slobodnih termina"
          )}
        </p>
      </IconTextRow>
    );
  }

  let scarcityMessage = "";
  const weeklySpots = summary.spotsAvailableThisWeek;

  if (weeklySpots != null) {
    switch (weeklySpots) {
      case 0:
        scarcityMessage = "Nema slobodnih termina ove nedelje";
        break;
      case 1:
        scarcityMessage = "Ostao je još 1 slobodan termin ove nedelje.";
        break;
      case 2:
        scarcityMessage = "Ostalo je još 2 slobodna termina ove nedelje.";
        break;
      case 3:
      case 4:
        scarcityMessage = `Ostalo je još ${weeklySpots} slobodna termina ove nedelje.`;
        break;
      default:
        if (weeklySpots < 10) {
          scarcityMessage = `Ostalo je još ${weeklySpots} slobodnih termina ove nedelje.`;
        }
        break;
    }
  }

  return (
    <div className="flex flex-col items-center space-x-1 space-y-1 text-muted-foreground md:flex-row md:space-y-0">
      <div className="flex items-center text-xs">
        <CalendarClock className="mr-2 h-4 w-4" />
        <span>{bookingMessage}</span>
      </div>
      {withScarcityMessage && (
        <p className="text-xs font-medium">{scarcityMessage}</p>
      )}
    </div>
  );
};

type ButtonVariant = VariantProps<typeof buttonVariants>["variant"];

export interface SelectTherapistProps {
  variant?: "default" | "outline" | "secondary";
  therapistSlug: string;
  label?: string;
  hideTeaser?: boolean;
  showCouplesOption?: boolean;
  couplesPrice?: number;
  defaultPrice?: number;
  forceStacked?: boolean;
  /** Render a compact segmented toggle instead of radio buttons (for tight spaces like mobile sticky bar) */
  compactSelector?: boolean;
  offersIntroCall?: boolean;
  /** Called when user switches between individual and couples therapy */
  onTherapyTypeChange?: (type: TherapyRelationshipType) => void;
}

const useTherapyRelationships = () => {
  const { data: sessionData } = useSession();
  return api.therapySessionRouter.getTherapyRelationships.useQuery(undefined, {
    enabled: sessionData?.user.role === "PATIENT",
  });
};

export default function SelectTherapist({
  variant = "default",
  therapistSlug,
  showCouplesOption,
  couplesPrice,
  defaultPrice,
  forceStacked,
  compactSelector,
  offersIntroCall,
  onTherapyTypeChange,
  ...props
}: SelectTherapistProps) {
  const searchParams = useSearchParams();
  const hasCouplesOption =
    showCouplesOption && !!couplesPrice && !!defaultPrice;
  const defaultTherapyType = hasCouplesOption
    ? resolveTherapyTypeQuery({
        therapyType: searchParams.get("therapyType"),
        couples: searchParams.get("couples"),
      })
    : TherapyRelationshipType.INDIVIDUAL;
  const [therapyType, setTherapyType] = useQueryState(
    "therapyType",
    parseAsStringEnum<TherapyRelationshipType>(
      Object.values(TherapyRelationshipType),
    ).withDefault(defaultTherapyType),
  );

  const handleTherapyTypeChange = (type: TherapyRelationshipType) => {
    void setTherapyType(type);
    onTherapyTypeChange?.(type);
  };
  const { data: sessionData } = useSession();
  const therapyRelationships = useTherapyRelationships();
  const { calculateDiscountedPrice } = useDiscount();

  const currentRelationship = useCallback(() => {
    return therapyRelationships.data?.find(
      (relationship) => relationship.therapist.slug === therapistSlug,
    );
  }, [therapyRelationships.data, therapistSlug]);

  const buttonClass = useMemo(
    () =>
      buttonVariants({
        variant,
        size: "lg",
        className: cn("w-full", !forceStacked && "md:w-auto"),
      }),
    [variant, forceStacked],
  );

  if (sessionData?.user.role === "THERAPIST") {
    return (
      <div
        className={cn("w-full space-y-2", !forceStacked && "md:w-auto")}
        title="Terapeuti ne mogu da zakažu sesije."
      >
        <div className={buttonClass}>
          {props.label ? props.label : "Izaberi me za svog terapeuta"}
          <ChevronRight className="ml-2 h-4 w-4" />
        </div>
        {props.hideTeaser ? null : (
          <TherapistTeaser therapistSlug={therapistSlug} />
        )}
      </div>
    );
  }

  if (currentRelationship()) {
    const url = `/app/inbox/${currentRelationship()?.id}`;
    return (
      <Link className={buttonClass} href={url}>
        Nastavi razgovor
        <ChevronRight className="ml-2 h-4 w-4" />
      </Link>
    );
  }

  return (
    <div className={cn("w-full space-y-3", !forceStacked && "md:w-auto")}>
      {hasCouplesOption && compactSelector && (
        <div className="flex rounded-lg border bg-muted p-1 text-sm">
          <button
            type="button"
            className={cn(
              "flex-1 rounded-md px-3 py-1.5 font-medium transition-colors",
              therapyType === TherapyRelationshipType.INDIVIDUAL
                ? "bg-background shadow-xs"
                : "text-muted-foreground",
            )}
            onClick={() =>
              handleTherapyTypeChange(TherapyRelationshipType.INDIVIDUAL)
            }
          >
            Individualna {EUR.format(calculateDiscountedPrice(defaultPrice))}
          </button>
          <button
            type="button"
            className={cn(
              "flex-1 rounded-md px-3 py-1.5 font-medium transition-colors",
              therapyType === TherapyRelationshipType.COUPLES
                ? "bg-background shadow-xs"
                : "text-muted-foreground",
            )}
            onClick={() =>
              handleTherapyTypeChange(TherapyRelationshipType.COUPLES)
            }
          >
            Parovi {EUR.format(calculateDiscountedPrice(couplesPrice))}
          </button>
        </div>
      )}
      {hasCouplesOption && !compactSelector && (
        <RadioGroup
          value={therapyType}
          onValueChange={handleTherapyTypeChange}
          className="gap-2"
        >
          <label
            className={cn(
              "flex cursor-pointer items-center gap-3 rounded-lg border p-3 text-sm transition-colors",
              therapyType === TherapyRelationshipType.INDIVIDUAL
                ? "border-primary bg-primary/5"
                : "bg-background hover:border-primary/50",
            )}
          >
            <RadioGroupItem value={TherapyRelationshipType.INDIVIDUAL} />
            <span className="flex-1 font-medium">Individualna terapija</span>
            <span className="font-semibold">
              {EUR.format(calculateDiscountedPrice(defaultPrice))}
            </span>
          </label>
          <label
            className={cn(
              "flex cursor-pointer items-center gap-3 rounded-lg border p-3 text-sm transition-colors",
              therapyType === TherapyRelationshipType.COUPLES
                ? "border-primary bg-primary/5"
                : "bg-background hover:border-primary/50",
            )}
          >
            <RadioGroupItem value={TherapyRelationshipType.COUPLES} />
            <span className="flex-1 font-medium">Terapija za parove</span>
            <span className="font-semibold">
              {EUR.format(calculateDiscountedPrice(couplesPrice))}
            </span>
          </label>
        </RadioGroup>
      )}
      <Link
        className={buttonVariants({
          variant,
          size: "lg",
          className: "w-full",
        })}
        href={`/terapeuti/${therapistSlug}/book?therapyType=${therapyType}`}
      >
        {props.label ? props.label : "Zakaži seansu"}
        <Armchair className="ml-2 h-4 w-4" />
      </Link>
      {props.hideTeaser ? null : (
        <TherapistTeaser therapistSlug={therapistSlug} />
      )}
      {offersIntroCall && (
        <p className="px-2 text-center text-xs leading-relaxed text-muted-foreground">
          Ako želiš prvo da vidiš da li ti terapeut odgovara,{" "}
          <Link
            className="font-medium text-primary underline-offset-4 hover:underline"
            href={`/terapeuti/${therapistSlug}/book?therapyType=${TherapyRelationshipType.INDIVIDUAL}&sessionPurpose=INTRO_CALL`}
            onClick={() =>
              capturePostHogEvent("intro_call_cta_clicked", {
                therapist_slug: therapistSlug,
                source_surface: "therapist_profile",
              })
            }
          >
            dostupan je besplatan uvodni razgovor od 15 min.
          </Link>
        </p>
      )}
    </div>
  );
}

export function SelectAiTherapist({
  variant = "default",
  therapistSlug,
  ...props
}: SelectTherapistProps) {
  const { data: sessionData } = useSession();
  const therapyRelationships = useTherapyRelationships();

  const buttonClass = useMemo(
    () =>
      buttonVariants({ variant, size: "lg", className: "w-full md:w-auto" }),
    [variant],
  );

  const AiTherapistTeaser = () => {
    return (
      <div className="flex flex-col items-center space-x-1 space-y-1 text-muted-foreground md:flex-row md:space-y-0">
        <div className="flex items-center text-xs">
          <CalendarClock className="mr-2 h-4 w-4" />
          <span>Dostupna 24/7 - Započni razgovor odmah</span>
        </div>
        <p className="text-xs font-medium">
          Prvih 40 poruka je besplatno.
        </p>
      </div>
    );
  };

  if (therapyRelationships.isLoading) {
    return (
      <Skeleton
        className={cn("h-[44px] w-full rounded-full md:w-[251px]", {
          "bg-primary": variant === "default",
          "bg-secondary": variant !== "default",
        })}
      />
    );
  }

  if (sessionData?.user.role === "THERAPIST") {
    return (
      <div
        className="w-full space-y-2 md:w-auto"
        title="Terapeuti ne mogu da zakažu sesije."
      >
        <div className={buttonClass}>
          {props.label ? props.label : "Izaberite me za svog terapeuta"}
          <ChevronRight className="ml-2 h-4 w-4" />
        </div>
        {props.hideTeaser ? null : <AiTherapistTeaser />}
      </div>
    );
  }

  return (
    <div className="w-full space-y-2 md:w-auto">
      <Link className={buttonClass} href="/tijana">
        {props.label ? props.label : "Započni razgovor sa Tijanom"}
        <ChevronRight className="ml-2 h-4 w-4" />
      </Link>
      {props.hideTeaser ? null : <AiTherapistTeaser />}
    </div>
  );
}
